import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { XMarkIcon, ExclamationTriangleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'

interface PaymentRejectState {
  slip: {
    id: string
    invoiceId: string
    studentName: string
    parentName: string
    amount: number
    uploadedAt: string
  }
}

export const PaymentRejectPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as PaymentRejectState
  const [reason, setReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // If no state provided, redirect back
  useEffect(() => {
    if (!state?.slip) {
      navigate('/admin/payments', { replace: true })
    }
  }, [state, navigate])

  if (!state?.slip) {
    return null // Will redirect in useEffect
  }

  const formatThaiBaht = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatThaiDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const commonReasons = [
    'จำนวนเงินไม่ตรงกับใบแจ้งหนี้',
    'รูปภาพไม่ชัดเจน ไม่สามารถอ่านได้',
    'ชื่อผู้โอนไม่ตรงกับชื่อผู้ปกครอง',
    'วันที่โอนเงินไม่อยู่ในช่วงที่กำหนด',
    'ข้อมูลในสลิปไม่ครบถ้วน',
    'สลิปการโอนถูกใช้ไปแล้ว',
    'เลขบัญชีปลายทางไม่ถูกต้อง',
    'อื่นๆ (ระบุเหตุผล)'
  ]

  const handleReasonChange = (selectedReason: string) => {
    setReason(selectedReason)
    if (selectedReason !== 'อื่นๆ (ระบุเหตุผล)') {
      setCustomReason('')
    }
  }

  const handleSubmit = async () => {
    const finalReason = reason === 'อื่นๆ (ระบุเหตุผล)' ? customReason : reason
    
    if (!finalReason.trim()) {
      return
    }

    setIsSubmitting(true)
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Navigate to success page with rejection details
      navigate('/admin/payments/reject/success', {
        state: {
          slip: state.slip,
          reason: finalReason,
          rejectedAt: new Date().toISOString()
        },
        replace: true
      })
    } catch (error) {
      console.error('Rejection failed:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const canSubmit = reason && (reason !== 'อื่นๆ (ระบุเหตุผล)' || customReason.trim())

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="ไม่อนุมัติการชำระเงิน"
        showBackButton={true}
        onBack={() => navigate('/admin/payments')}
      />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Warning Message */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-red-800 mb-1">คำเตือน</h3>
              <p className="text-sm text-red-700">
                การไม่อนุมัติการชำระเงินจะส่งการแจ้งเตือนไปยังผู้ปกครอง
                กรุณาระบุเหตุผลอย่างชัดเจนเพื่อให้ผู้ปกครองสามารถแก้ไขและส่งสลิปใหม่ได้
              </p>
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">รายละเอียดการชำระเงิน</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ใบแจ้งหนี้:</span>
              <span className="font-medium">{state.slip.invoiceId}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">นักเรียน:</span>
              <span className="font-medium">{state.slip.studentName}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ผู้ปกครอง:</span>
              <span className="font-medium">{state.slip.parentName}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">จำนวนเงิน:</span>
              <span className="font-medium text-blue-600">{formatThaiBaht(state.slip.amount)}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">วันที่ส่งสลิป:</span>
              <span className="font-medium">{formatThaiDateTime(state.slip.uploadedAt)}</span>
            </div>
          </div>
        </div>

        {/* Reason Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">เหตุผลที่ไม่อนุมัติ</h2>
          
          <div className="space-y-3">
            {commonReasons.map((commonReason, index) => (
              <label key={index} className="flex items-start p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="reason"
                  value={commonReason}
                  checked={reason === commonReason}
                  onChange={(e) => handleReasonChange(e.target.value)}
                  className="h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500 mt-1"
                />
                <span className="ml-3 text-sm text-gray-700 leading-5">{commonReason}</span>
              </label>
            ))}
          </div>

          {/* Custom Reason Input */}
          {reason === 'อื่นๆ (ระบุเหตุผล)' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ระบุเหตุผลอื่นๆ <span className="text-red-500">*</span>
              </label>
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="กรุณาระบุเหตุผลที่ไม่อนุมัติการชำระเงิน..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                เหตุผลนี้จะถูกส่งไปยังผู้ปกครองเพื่อให้ทราบและแก้ไข
              </p>
            </div>
          )}
        </div>

        {/* Preview */}
        {canSubmit && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-yellow-800 mb-2">ตัวอย่างข้อความที่จะส่งไปยังผู้ปกครอง:</h3>
            <div className="text-sm text-yellow-700 bg-yellow-100 p-3 rounded border">
              <p className="font-medium">🏫 โรงเรียนกวดวิชา</p>
              <p className="mt-2">เรียน คุณ{state.slip.parentName}</p>
              <p className="mt-1">
                ขออภัย สลิปการโอนเงินสำหรับใบแจ้งหนี้ {state.slip.invoiceId} 
                ไม่สามารถอนุมัติได้
              </p>
              <p className="mt-2 font-medium">เหตุผล:</p>
              <p>{reason === 'อื่นๆ (ระบุเหตุผล)' ? customReason : reason}</p>
              <p className="mt-2">กรุณาตรวจสอบและส่งสลิปใหม่อีกครั้ง</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/admin/payments')}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            ยกเลิก
          </button>
          
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                กำลังส่ง...
              </>
            ) : (
              <>
                <XMarkIcon className="h-5 w-5 mr-2" />
                ยืนยันไม่อนุมัติ
              </>
            )}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            💡 เคล็ดลับ: ระบุเหตุผลที่ชัดเจนจะช่วยให้ผู้ปกครองแก้ไขและส่งสลิปที่ถูกต้องได้เร็วขึ้น
          </p>
        </div>
      </div>
    </div>
  )
}